import React, { useEffect, useState } from "react";
import { ImCross } from "react-icons/im";
import { IoMdSearch } from "react-icons/io";
import Pagination from "../components/Pagination";

const EsimDataTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [renewalConfirmation, setRenewalConfirmation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Renew data map keyed by orderID
  const [renewDataMap, setRenewDataMap] = useState({});
  const apiUrl = process.env.REACT_APP_BASE_URL; // Use the environment variable

  const fetchEsimData = async () => {
    const partnerCode = localStorage.getItem("partnerCode");
    try {
      const response = await fetch(
        `${apiUrl}b2b-manual/GetPagedManualB2b?pageIndex=1&pageSize=5000&partner_Code=${112722282}`,
        {
          method: "GET",
          headers: { 
            Accept: "application/json",
          },
        }
      );

      const result = await response.json();
      if (result.isSuccess) {
        setData(result.items);
      } else {
        console.error("API Error:", result.message);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch renewal data from the admin API
  const fetchRenewData = async () => {
    const partnerCode = localStorage.getItem("partnerCode");
    if (!partnerCode) return;

    try {
      const response = await fetch(`${apiUrl}Renew/GetRenewData`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          partnerCode: "112722282",
          pageSize: 5000,
          pageIndex: 1,
        }),
      });

      const result = await response.json();
      if (result.isSuccess && Array.isArray(result.renewData)) {
        // Map renewData by orderID for quick lookup
        const map = {};
        result.renewData.forEach(item => {
          if (item.orderID) {
            map[item.orderID] = item;
          }
        });
        setRenewDataMap(map);
      } else {
        setRenewDataMap({});
        console.error("Renew data API error:", result.message);
      }
    } catch (error) {
      setRenewDataMap({});
      console.error("Renew data fetch error:", error);
    }
  };

  useEffect(() => {
    fetchEsimData();
    fetchRenewData();
  }, []);

  // Filter data based on search query
  const filteredData = data.filter(item => {
    const orderId = String(item.ordeR_ID || '').toLowerCase();
    const planName = String(item.plaN_NAME || '').toLowerCase();
    const phoneNumber = String(item.phonE_NUMBER || '').toLowerCase();
    const searchTerm = searchQuery.toLowerCase();

    return orderId.includes(searchTerm) || planName.includes(searchTerm) || phoneNumber.includes(searchTerm);
  });

  // Pagination helpers
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleRenew = (plan) => {
    const cleanPrice = plan.pricE_USD?.replace(/[^0-9.]/g, "");
    const priceUSD = parseFloat(cleanPrice);

    if (isNaN(priceUSD)) {
      alert(
        `Invalid price for Order ID ${plan.ordeR_ID}. Raw Price: ${plan.pricE_USD}`
      );
      return;
    }

    setSelectedPlan({
      ...plan,
      order_id: plan.ordeR_ID,
      purchaseamount: priceUSD,
    });
    setIsModalOpen(true);
    setRenewalConfirmation(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  const requestRenewal = async (plan, month) => {
    const payload = {
      flag: 2,
      userAmount: (plan.purchaseamount * month).toFixed(2),
      orderID: String(plan.order_id),
      renewalDays: month * 30,
    };

    try {
      const response = await fetch(
        `${apiUrl}Renew/InsertRenew`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.isSuccess) {
        setRenewalConfirmation({
          message: `Renewal failed: ${result.message || "Unknown error"}`,
          success: false,
        });
        return;
      }

      setRenewalConfirmation({
        message: "Renewal successful!",
        success: true,
      });

      fetchEsimData(); // Refresh table
      fetchRenewData(); // Refresh renew data to keep admin expiry dates updated
    } catch (error) {
      setRenewalConfirmation({
        message: "An error occurred while sending the request.",
        success: false,
      });
      console.error("Request failed:", error);
    }
  };

  const getExpiryDate = (activationDateStr, monthsToAdd) => {
    if (!activationDateStr) return "Invalid Date";
    const [datePart] = activationDateStr.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    const activationDate = new Date(year, month - 1, day);
    if (isNaN(activationDate.getTime())) return "Invalid Date";
    const expiryDate = new Date(activationDate);
    expiryDate.setMonth(expiryDate.getMonth() + monthsToAdd);
    return expiryDate.toLocaleDateString("en-GB");
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-4">
        <h1 className="text-5xl font-bold mb-4">eSIM Orders</h1>
        <div className="bg-[#117575] p-1 w-96 rounded-xl">
          <div className="relative">
            <input
              type="text"
              placeholder="Search Order ID, Plan Name, Phone Number....."
              className="w-full rounded-2xl bg-brand-gray h-8 pl-10"
              value={searchQuery}
              onChange={handleSearch}
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <IoMdSearch size={20} className="text-gray-500" />
            </div>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center text-gray-500">Loading...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border border-gray-200 shadow-md rounded-lg">
              <thead className="bg-gray-100">
              
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr
                    key={item.id}
                    className="border-t border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="px-4 py-2">{item.ordeR_ID}</td>
                    <td className="px-4 py-2">{item.country}</td>
                    <td className="px-4 py-2">{item.plaN_NAME}</td>
                    <td className="px-4 py-2">{item.pricE_USD}</td>
                    <td className="px-4 py-2">{item.activatioN_DATE.split(' ')[0]}</td>
                    <td className="px-4 py-2">{item.phonE_NUMBER}</td>
                    <td className="px-4 py-2">
                      {item.renew === "Yes" ? (
                        <button
                          onClick={() => handleRenew(item)}
                          className="rounded-lg bg-[#117575] items-center p-1 m-1 text-brand-white"
                        >
                          Renew
                        </button>
                      ) : (
                        item.renew
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          /> 
          
          <div className="mt-2 text-center text-gray-600">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
          </div>
        </>
      )}

      {/* MODAL */}
      {isModalOpen && selectedPlan && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 md:w-2/3 h-5/6 relative shadow-lg flex flex-col">
            <h1 className="text-2xl md:text-3xl font-bold mb-4 text-center text-gray-900">
              Please select Order ID
            </h1>

            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 text-gray-600 hover:text-gray-800 transition"
            >
              <ImCross size={20} />
            </button>

            <div className="overflow-y-auto flex-1">
              {!renewalConfirmation ? (
                <table className="min-w-full table-auto mb-4 text-gray-700 border">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr className="border-b">
                      <th className="border px-4 py-2 text-center">Order ID</th>
                      <th className="border px-4 py-2 text-center">Subscription Month</th>
                      <th className="border px-4 py-2 text-center">Amount</th>
                      <th className="border px-4 py-2 text-center">Expiry</th>
                      <th className="border px-4 py-2 text-center">Admin Expiry Date</th> {/* New column */}
                      <th className="border px-4 py-2 text-center">Renewal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(12)].map((_, i) => {
                      const month = i + 1;
                      const baseAmount = selectedPlan.purchaseamount;
                      const expiryDate = getExpiryDate(
                        selectedPlan.activatioN_DATE,
                        month
                      );
                      
                      // Get admin expiry date from renewDataMap by order_id
                      const adminData = renewDataMap[selectedPlan.order_id];
                      let adminExpiryDisplay = "sorry adminsayno";
                      if (adminData && adminData.newExpiryDate) {
                        // Format admin expiry date to DD/MM/YYYY
                        const adminDate = new Date(adminData.newExpiryDate);
                        if (!isNaN(adminDate.getTime())) {
                          adminExpiryDisplay = adminDate.toLocaleDateString("en-GB");
                        }
                      }

                      return (
                        <tr key={month} className="border-b hover:bg-gray-50">
                          <td className="border px-4 py-2 text-center">
                            {selectedPlan.order_id}
                          </td>
                          <td className="border px-4 py-2 text-center">
                            {month} Month{month > 1 ? "s" : ""}
                          </td>
                          <td className="border px-4 py-2 text-center">
                            ${baseAmount.toFixed(2)} x {month}
                          </td>
                          <td className="border px-4 py-2 text-center">
                            {expiryDate}
                          </td>
                          <td className="border px-4 py-2 text-center">
                            {adminExpiryDisplay}
                          </td>
                          <td className="border px-4 py-2 text-center">
                            <button
                              onClick={() =>
                                requestRenewal(selectedPlan, month)
                              }
                              className="rounded-lg bg-[#117575] items-center p-2 m-1 text-brand-white"
                            >
                              ${parseFloat((baseAmount * month).toFixed(2))} Renew
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div
                  className={`mt-4 text-center font-semibold ${
                    renewalConfirmation.success
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  <p>{renewalConfirmation.message}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EsimDataTable;
